import { memo, FC, useCallback } from "react";
import { Pressable } from "react-native";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useNumDays, usePageOffset, type GridStore } from "../Grids/grid-store";
import { MAX_VISIBLE_DAYS } from "../Grids/constants";
import { StyleSheet, useUnistyles } from "react-native-unistyles";

interface GridActionsProps {
  store: GridStore;
  needsPagination: boolean;
}

export const GridActions: FC<GridActionsProps> = memo(({ store, needsPagination }) => {
  const { theme } = useUnistyles();
  const numDays = useNumDays(store);
  const pageOffset = usePageOffset(store);
  const maxOffset = Math.max(0, numDays - MAX_VISIBLE_DAYS);
  const canGoBack = pageOffset > 0;
  const canGoForward = pageOffset < maxOffset;

  const handleClearSelection = useCallback(() => {
    store.getState().resetCells();
  }, [store]);

  const handlePageBack = useCallback(() => {
    const current = store.getState().pageOffset;
    store.getState().setPageOffset(Math.max(0, current - MAX_VISIBLE_DAYS));
  }, [store]);

  const handlePageForward = useCallback(() => {
    const { pageOffset: current, numDays: total } = store.getState();
    const max = Math.max(0, total - MAX_VISIBLE_DAYS);
    store.getState().setPageOffset(Math.min(max, current + MAX_VISIBLE_DAYS));
  }, [store]);

  return (
    <>
      {needsPagination && (
        <View alignItems="center">
          <Text textStyle="xs/regular" color="textMuted">
            {pageOffset + 1}–{Math.min(pageOffset + MAX_VISIBLE_DAYS, numDays)} of {numDays}
          </Text>
        </View>
      )}
      <View pt="md" flexDirection="row" justifyContent="center" alignItems="center" style={{ gap: theme.spacing.md }}>
        {needsPagination && (
          <Pressable
            onPress={handlePageBack}
            disabled={!canGoBack}
            style={[styles.arrowButton, { opacity: canGoBack ? 1 : 0.4 }]}
          >
            <Icon name="chevronLeft" size={18} color={theme.colors.text as string} />
          </Pressable>
        )}

        <Pressable onPress={handleClearSelection} style={styles.clearSelectionButton}>
          <Icon name="undo" size={16} color={theme.colors.text as string} />
          <Text textStyle="sm/regular" color="text">
            Clear selection
          </Text>
        </Pressable>

        {needsPagination && (
          <Pressable
            onPress={handlePageForward}
            disabled={!canGoForward}
            style={[styles.arrowButton, { opacity: canGoForward ? 1 : 0.4 }]}
          >
            <Icon name="chevronRight" size={18} color={theme.colors.text as string} />
          </Pressable>
        )}
      </View>
    </>
  );
});

const styles = StyleSheet.create((theme) => ({
  clearSelectionButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
  },
  arrowButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.interactive.buttons.default_background,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
    alignItems: "center",
    justifyContent: "center",
  },
}));
